import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useSubscription } from '../../hooks/useSubscription'
import { Button } from '../ui/Button'

export function Header() {
  const { user, signOut, isAdmin } = useAuth()
  const { getActivePlan, isFreeTrial, getRemainingTrialImages, trialStatus } = useSubscription()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const activePlan = getActivePlan()
  const isFreeTrialUser = isFreeTrial()
  const remainingTrialImages = getRemainingTrialImages()

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  return (
    <header className="bg-gradient-to-r from-slate-50 via-blue-50 to-cyan-50 shadow-md border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24 sm:h-28">
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img
                src="/images/copy_of_grunge_urban_spray_music_studio_logo_(2).png"
                alt="Social Pack"
                className="h-20 sm:h-24 w-auto"
              />
            </Link>
          </div>

          <nav className="hidden lg:flex items-center space-x-4">
            <Link to="/blog" className="text-gray-600 hover:text-gray-900">
              Blog
            </Link>
            <Link to="/faq" className="text-gray-600 hover:text-gray-900">
              FAQ
            </Link>
            <Link to="/pricing" className="text-gray-600 hover:text-gray-900">
              Pricing
            </Link>

            {user ? (
              <div className="flex items-center space-x-4">
                <Link to="/generator" className="text-gray-600 hover:text-gray-900">
                  Generator
                </Link>
                <Link to="/library" className="text-gray-600 hover:text-gray-900">
                  Library
                </Link>
                <Link to="/account" className="text-gray-600 hover:text-gray-900">
                  Account
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="text-gray-600 hover:text-gray-900 font-medium">
                    Admin
                  </Link>
                )}
                {isFreeTrialUser ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
                      Free Trial
                    </span>
                    {trialStatus?.has_used_trial ? (
                      <span className="text-xs text-gray-500">
                        (Used)
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600 font-medium">
                        {remainingTrialImages} images
                      </span>
                    )}
                  </div>
                ) : activePlan ? (
                  <span className="text-sm bg-throttle-cyan-100 text-throttle-cyan-900 px-2 py-1 rounded-full">
                    {activePlan}
                  </span>
                ) : null}
                <Button variant="outline" onClick={signOut}>
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost">Sign In</Button>
                </Link>
                <Link to="/signup">
                  <Button>Sign Up</Button>
                </Link>
              </div>
            )}
          </nav>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-throttle-cyan-500"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={closeMobileMenu}
          />
          <div className="fixed top-24 sm:top-28 left-0 right-0 bottom-0 bg-white z-50 lg:hidden overflow-y-auto">
            <nav className="px-4 py-6 space-y-4">
              <Link
                to="/blog"
                className="block py-3 px-4 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={closeMobileMenu}
              >
                Blog
              </Link>
              <Link
                to="/faq"
                className="block py-3 px-4 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={closeMobileMenu}
              >
                FAQ
              </Link>
              <Link
                to="/pricing"
                className="block py-3 px-4 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={closeMobileMenu}
              >
                Pricing
              </Link>

              {user ? (
                <>
                  {activePlan && (
                    <div className="py-3 px-4 bg-throttle-cyan-50 rounded-lg">
                      <span className="text-sm font-medium text-throttle-cyan-900">
                        {activePlan} Plan
                      </span>
                    </div>
                  )}
                  <Link
                    to="/generator"
                    className="block py-3 px-4 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={closeMobileMenu}
                  >
                    Generator
                  </Link>
                  <Link
                    to="/library"
                    className="block py-3 px-4 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={closeMobileMenu}
                  >
                    Library
                  </Link>
                  <Link
                    to="/account"
                    className="block py-3 px-4 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={closeMobileMenu}
                  >
                    Account
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="block py-3 px-4 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                      onClick={closeMobileMenu}
                    >
                      Admin
                    </Link>
                  )}
                  <div className="pt-4 border-t border-gray-200">
                    <Button
                      variant="outline"
                      onClick={() => {
                        signOut()
                        closeMobileMenu()
                      }}
                      className="w-full"
                    >
                      Sign Out
                    </Button>
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block"
                  >
                    <Button variant="ghost" className="w-full">Sign In</Button>
                  </Link>
                  <Link
                    to="/signup"
                    onClick={closeMobileMenu}
                    className="block"
                  >
                    <Button className="w-full">Sign Up</Button>
                  </Link>
                </div>
              )}
            </nav>
          </div>
        </>
      )}
    </header>
  )
}